import React from "react";
//#region types
import { InvoiceFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { SelfInvoiceFormDimensionsSectionProps } from "models/propTypes/selfInvoicePropTypes";
import { DimensionEnums } from "models/enums";
import { CurrencyCode } from "models/enums/currency";
//#endregion
//#region components
import {
  ExpandableSection,
  ExpandableSectionSummary,
  DimensionChipSelector,
  FormFieldLayout,
  Description,
} from "components/common/wintForms";
import { StaticInfoMessage, StyledButton, useDimensionMaps } from "components/common";
import { Grid } from "@mui/material";
import { useSelfInvoiceService } from "serviceHooks";
//#endregion
import { LocalizationHelperInstance } from "utils/helpers";

const SelfInvoiceTodoFormDimensionsSection: React.FC<
  SelfInvoiceFormDimensionsSectionProps<InvoiceFormTypes.SelfInvoiceTodo>
> = ({ connect, expandableStepProps, values }) => {
  const selfInvoiceService = useSelfInvoiceService();

  const { dimensions, forcedDimensionMap } = useDimensionMaps(DimensionEnums.DimensionCategory.Invoice);
  const allowedDimensionIds = React.useMemo(() => dimensions.flatMap((d) => d.value).map((d) => d.id), [dimensions]);

  const dimensionFieldProps = connect("dimensions");
  const isOptional = forcedDimensionMap.length === 0;
  const dimensionsTotalAmount = React.useMemo(
    () => selfInvoiceService.getInvoiceItemTotal(values.products ?? []),
    [values.products]
  );
  const readOnlyDimension = values.isDimensionsAdvancedConfiguration;

  const onChangeDimensions = () => {
    connect("isDimensionsAdvancedConfiguration").setValue(false);
    dimensionFieldProps.setValue([]);
  };

  return dimensions?.length > 0 ? (
    <ExpandableSection
      summary={
        <ExpandableSectionSummary
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Tags)}
          subText={
            isOptional ? LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Optional) : undefined
          }
        />
      }
      initiallyOpen={!isOptional}
      hasErrors={dimensionFieldProps.hasErrors}
      {...expandableStepProps}
    >
      <Grid container rowGap={1}>
        <Description horizontal>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.SelfInvoiceDimensionsDescription)}
        </Description>
        {readOnlyDimension ? (
          <StaticInfoMessage>
            {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.InvoiceHasRowLevelDimensions)}
          </StaticInfoMessage>
        ) : (
          <></>
        )}
        <DimensionChipSelector
          {...dimensionFieldProps}
          value={
            values.isDimensionsAdvancedConfiguration
              ? selfInvoiceService
                  .getRowDimensionData(values.products)
                  ?.filter((d) => allowedDimensionIds.includes(d.dimensionId))
              : dimensionFieldProps.value
          }
          type={DimensionEnums.DimensionCategory.Invoice}
          layout={<FormFieldLayout noOuterSpacing />}
          currency={values.currency ?? CurrencyCode.SEK}
          amount={dimensionsTotalAmount}
          hideViewAll
          enableSingleSelectionMode
          readonly={readOnlyDimension}
          allowDefaultDimensions
        />
        {readOnlyDimension ? (
          <Grid item xs>
            <StyledButton size="xs" onClick={onChangeDimensions}>
              {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Invoice.ChangeDimensions)}
            </StyledButton>
          </Grid>
        ) : (
          <></>
        )}
      </Grid>
    </ExpandableSection>
  ) : (
    <></>
  );
};

export default SelfInvoiceTodoFormDimensionsSection;
